import React, { useState, useEffect, useMemo } from "react";
import {
  useNavigate,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  UserCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api, { recruiterReq } from "../lib/api";
import { toast } from "sonner";
import RecruiterOverview from "./recruiter/Overview";
import RecruiterReferrals from "./recruiter/Referrals";
import RecruiterNew from "./recruiter/NewReferral";
import RecruiterProfile from "./recruiter/Profile";

export default function RecruiterDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [me, setMe] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [m, a] = await Promise.all([
        api.get("/recruiters/me", recruiterReq()),
        api.get("/recruiters/me/applications", recruiterReq()),
      ]);
      setMe(m.data);
      setApps(a.data);
    } catch {
      toast.error("Session expired. Please sign in again.");
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("cct_recruiter_token");
    localStorage.removeItem("cct_recruiter");
    navigate("/recruiter/login");
  };

  const submitReferral = async (form, idDoc, supDoc) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
    if (idDoc) fd.append("id_document", idDoc);
    if (supDoc) fd.append("supporting_document", supDoc);
    await api.post("/recruiters/me/applications", fd, recruiterReq());
    toast.success("Referral submitted");
    load();
  };

  const items = useMemo(
    () => [
      {
        to: "/recruiter/dashboard",
        end: true,
        icon: LayoutDashboard,
        label: "Overview",
      },
      {
        to: "/recruiter/dashboard/referrals",
        icon: Users,
        label: "My Referrals",
        badge: apps.length,
      },
      {
        to: "/recruiter/dashboard/new",
        icon: UserPlus,
        label: "New Referral",
      },
      {
        to: "/recruiter/dashboard/profile",
        icon: UserCircle2,
        label: "Profile",
      },
    ],
    [apps]
  );

  const titleMap = {
    "/recruiter/dashboard": [
      "Overview",
      "Track your referrals and partner performance",
    ],
    "/recruiter/dashboard/referrals": [
      "My Referrals",
      "Applications submitted on behalf of students",
    ],
    "/recruiter/dashboard/new": [
      "New Referral",
      "Submit a new student application",
    ],
    "/recruiter/dashboard/profile": [
      "Profile",
      "Your partner account and verification",
    ],
  };
  const [pageTitle, pageSubtitle] = titleMap[location.pathname] || [
    "Overview",
    "",
  ];

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading...
      </div>
    );

  return (
    <div className="min-h-screen bg-cct-ambient">
      <div className="flex">
        <Sidebar
          items={items}
          user={me}
          roleLabel="Recruiter"
          onLogout={logout}
          collapsed={collapsed}
        />
        <div className="flex-1 min-w-0">
          <Topbar
            title={pageTitle}
            subtitle={pageSubtitle}
            onToggleSidebar={() => setCollapsed((c) => !c)}
          />
          <main className="p-5 sm:p-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Routes>
                  <Route
                    index
                    element={<RecruiterOverview me={me} apps={apps} />}
                  />
                  <Route
                    path="referrals"
                    element={<RecruiterReferrals apps={apps} />}
                  />
                  <Route
                    path="new"
                    element={
                      <RecruiterNew
                        onSubmit={submitReferral}
                        onDone={() =>
                          navigate("/recruiter/dashboard/referrals")
                        }
                      />
                    }
                  />
                  <Route
                    path="profile"
                    element={<RecruiterProfile me={me} />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/recruiter/dashboard" replace />}
                  />
                </Routes>
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
}
